// Combines report grouping and date-range selection.
// Users choose days, weeks, or months as the report's periods and use the calendar
// to choose which dates the report covers. Setups use only the date range.
import {
    PeriodOption,
    ReportSettings,
    Resolution,
    ResolutionOptions,
} from '../report-settings/reportSettings.ts';
import { PeriodOptionSet } from './createPeriodOptions.ts';
import { getDefaultPeriodRange } from './defaultPeriodRange.ts';
import { getPeriods } from './getPeriods.ts';
import { findPeriodRangeForDates } from './periodRanges.ts';
import { ReportDateRangeControl } from './reportDateRangeControl.tsx';


interface Props {
    reportSettings: ReportSettings;
    setReportSettings: (reportSettings: ReportSettings) => void;
    periodOptionSet: PeriodOptionSet;
}

export function ReportTimeControls({
    reportSettings,
    setReportSettings,
    periodOptionSet,
}: Props) {
    const isSetupsReport = reportSettings.reportType === 'Setups';

    function handleResolutionSelect(resolution: Resolution) {
        const isSameResolution = resolution === reportSettings.resolution;
        if (isSameResolution) return;
        const periods = getPeriods(periodOptionSet, resolution);
        // Switching Days/Weeks/Months keeps the dates covered by the current report.
        // For example, two days within one week become that whole week when switching
        // to Weeks, because reports select whole periods.
        const mappedRange = findPeriodRangeForDates(
            periods,
            reportSettings.first.start,
            reportSettings.last.end,
        );
        // Fall back to recent periods only if the selected report dates do not overlap
        // any available periods at the newly chosen resolution.
        const rangeSettings = mappedRange
            ? {
                first: periods[mappedRange.firstIndex],
                last: periods[mappedRange.lastIndex],
                periodsInRange: periods.slice(mappedRange.firstIndex, mappedRange.lastIndex + 1),
            }
            : getDefaultPeriodRange(periodOptionSet, resolution);

        // Graphs use both the first/last periods and periodsInRange, the periods between
        // them. Update both representations together when changing the grouping.
        setReportSettings({
            ...reportSettings,
            resolution,
            ...rangeSettings,
        });
    }

    function handlePeriodChange(resolution: Resolution, first: PeriodOption, last: PeriodOption) {
        const selectedPeriods = getPeriods(periodOptionSet, resolution);
        const firstIndex = selectedPeriods.findIndex(
            (option) => option.start.getTime() === first.start.getTime(),
        );
        const lastIndex = selectedPeriods.findIndex(
            (option) => option.start.getTime() === last.start.getTime(),
        );
        const isInvalidRange = firstIndex === -1 || lastIndex === -1 || firstIndex > lastIndex;
        if (isInvalidRange) return;

        setReportSettings({
            ...reportSettings,
            resolution,
            first,
            last,
            periodsInRange: selectedPeriods.slice(firstIndex, lastIndex + 1),
        });
    }

    return (
        <section className='report-controls__section'>
            <div className='report-controls__heading'>
                {isSetupsReport ? 'Date range' : 'Time period'}
            </div>
            {/* Resolution changes here apply immediately; dialog changes wait for Apply. */}
            {!isSetupsReport && <div className='report-resolution-selector' role='group' aria-label='Report period type'>
                {ResolutionOptions.map((value) => (
                    <button
                        key={value}
                        type='button'
                        className={reportSettings.resolution === value ? 'selected' : undefined}
                        aria-pressed={reportSettings.resolution === value}
                        onClick={() => handleResolutionSelect(value)}
                    >
                        {value}
                    </button>
                ))}
            </div>}
            <div className='report-period-range'>
                <ReportDateRangeControl
                    first={reportSettings.first}
                    last={reportSettings.last}
                    periodOptionSet={periodOptionSet}
                    resolution={reportSettings.resolution}
                    hideResolution={isSetupsReport}
                    onChange={handlePeriodChange}
                />
            </div>
        </section>
    );
}
